import { rgba, lighten, darken, desaturate, saturate, mix } from "polished";

// FONTS
export const fonts = {
  main: '"Open Sans", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
};
export const font = fonts.main;
export const fontFamilies = fonts;
export const fontSizes = [
  "1.1rem",
  "1.2rem",
  "1.3rem",
  "1.6rem",
  "1.8rem",
  "2.2rem",
  "2.6rem",
  "3.0rem",
  "3.4rem",
  "4.4rem",
  "6rem",
];
export const fontWeights = [300, 400, 600];
export const lineHeights = [1, 1.5, 2];

// Z-AXIS spec
export const zIndices = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 50];

// SPACING
export const space = [
  0,
  "1px",
  "2px",
  "6px",
  "12px",
  "16px",
  "24px",
  "40px",
  "100px",
  "150px",
  "300px",
];

space.xxs = space[1];
space.xs = space[2];
space.s = space[3];
space.r = space[4];
space.l = space[5];
space.xl = space[6];
space.xxl = space[7];
space.xxxl = space[8];

// SIZES (for LAYOUT properties)
export const sizes = [
  0,
  "1px",
  "2px",
  "4px",
  "8px",
  "12px",
  "16px",
  "32px",
  "64px",
  "128px",
  "256px",
];

sizes.xxs = sizes[1];
sizes.xs = sizes[2];
sizes.s = sizes[3];
sizes.r = sizes[4];
sizes.l = sizes[5];
sizes.xl = sizes[6];
sizes.xxl = sizes[7];
sizes.xxxl = sizes[8];

// NEW ACCESSIBLE COLOUR PALETTE
const black = "#000";
const white = "#fff";
const greyBackground = "#F2F4F7";
const grey = "#90979A"; // Base grey - Mainly used for UI elements like backgrounds, muted text, icons, dividers or borders
const primary = "#007ac4"; // Potential new colour: #2a5ec1. Base primary brand colour (blue) - Mainly used for buttons, links and selected states
const secondary = "#8714f2"; // Change to #7211ED with new colour palette. Potential new purple colour. Introduce new secondary colour maybe purple when we re-design our colour palette
//const tertiary = "#FF9E44"; // A tertiary colour for limited use. Not sure if needed.
const success = "#00883b"; // Base brand success colour (green) - Mainly used to indicate success, status=good, valid, add, or save states
const successDesaturated = desaturate(0.3, success); // This is because the lighter versions of success were too saturated and 'minty' coloured.
const warning = "#FF8C00"; // Base brand warning colour - Mainly used to indicate warnings, slipping or near due states
const warningSaturated = saturate(2, warning); // This is because the darker versions of warning were too under saturated and mustard looking.
const danger = "#d90f27"; // Base brand danger/error colour - Mainly used to indicate error, status=bad, at risk or over due states

// Pre-compute greyscale palette once so the semantic tokens below can alias
// the exact same values (single source of truth — tweaking, say, greyLight
// will also update any semantic token that aliases it, such as mutedText).
const greyMid = lighten(0.15, grey);
const greyLight = lighten(0.2, grey);
const greyLighter = lighten(0.3, grey);
const greyLightest = lighten(0.35, grey);
const greyDark = darken(0.15, grey);
const greyDarker = darken(0.3, grey);
const greyDarkest = darken(0.5, grey);

const black90 = rgba(black, 0.9);
const black80 = rgba(black, 0.8);
const black70 = rgba(black, 0.7);
const black60 = rgba(black, 0.6);
const black50 = rgba(black, 0.5);
const black40 = rgba(black, 0.4);
const black30 = rgba(black, 0.3);
const black20 = rgba(black, 0.2);
const black10 = rgba(black, 0.1);

const white90 = rgba(white, 0.9);
const white80 = rgba(white, 0.8);
const white70 = rgba(white, 0.7);
const white60 = rgba(white, 0.6);
const white50 = rgba(white, 0.5);
const white40 = rgba(white, 0.4);
const white30 = rgba(white, 0.3);
const white20 = rgba(white, 0.2);
const white10 = rgba(white, 0.1);

const primaryLight = lighten(0.2, primary);
const primaryLighter = lighten(0.35, primary);
const primaryLightest = lighten(0.55, primary);
const primaryDark = darken(0.1, primary);
const primaryDarker = darken(0.25, primary);
const primaryDarkest = darken(0.35, primary);
const primary10 = rgba(primary, 0.1);
const primary20 = rgba(primary, 0.2);
const primary30 = rgba(primary, 0.3);
const primary40 = rgba(primary, 0.4);
const primary50 = rgba(primary, 0.5);
const primary60 = rgba(primary, 0.6);
const primary70 = rgba(primary, 0.7);
const primary80 = rgba(primary, 0.8);
const primary90 = rgba(primary, 0.9);

const secondaryLight = lighten(0.1, secondary);
const secondaryLighter = lighten(0.2, secondary);
const secondaryLightest = lighten(0.35, secondary);
const secondaryEvenLighter = lighten(0.4, secondary);
const secondaryDark = darken(0.1, secondary);
const secondaryDarker = darken(0.2, secondary);
const secondaryDarkest = darken(0.3, secondary);
const secondary10 = rgba(secondary, 0.1);
const secondary20 = rgba(secondary, 0.2);
const secondary30 = rgba(secondary, 0.3);
const secondary40 = rgba(secondary, 0.4);
const secondary50 = rgba(secondary, 0.5);
const secondary60 = rgba(secondary, 0.6);
const secondary70 = rgba(secondary, 0.7);
const secondary80 = rgba(secondary, 0.8);
const secondary90 = rgba(secondary, 0.9);

const successLight = lighten(0.11, successDesaturated);
const successLighter = lighten(0.25, successDesaturated);
const successLightest = lighten(0.5, successDesaturated);
const successEvenLighter = lighten(0.55, successDesaturated);
// Very pale green for subtle card/surface fills. lighten() keeps the green minty
// even when light, so mix toward white for a soft, low-saturation pastel.
const successPale = mix(0.9, white, successDesaturated);
const successDark = darken(0.1, success);
const successDarker = darken(0.2, success);
const successDarkest = darken(0.3, success);
const success10 = rgba(success, 0.1);
const success20 = rgba(success, 0.2);
const success30 = rgba(success, 0.3);
const success40 = rgba(success, 0.4);
const success50 = rgba(success, 0.5);
const success60 = rgba(success, 0.6);
const success70 = rgba(success, 0.7);
const success80 = rgba(success, 0.8);
const success90 = rgba(success, 0.9);

const warningLight = lighten(0.15, warning);
const warningLighter = lighten(0.25, warning);
const warningLightest = lighten(0.35, warning);
// Very pale orange for subtle card/surface fills (paler than warningLightest).
const warningPale = mix(0.9, white, warning);
const warningDark = darken(0.1, warningSaturated);
const warningDarker = darken(0.2, warningSaturated);
const warningDarkest = darken(0.4, warningSaturated);
const warning10 = rgba(warning, 0.1);
const warning20 = rgba(warning, 0.2);
const warning30 = rgba(warning, 0.3);
const warning40 = rgba(warning, 0.4);
const warning50 = rgba(warning, 0.5);
const warning60 = rgba(warning, 0.6);
const warning70 = rgba(warning, 0.7);
const warning80 = rgba(warning, 0.8);
const warning90 = rgba(warning, 0.9);

const dangerLight = lighten(0.1, danger);
const dangerLighter = lighten(0.2, danger);
const dangerLightest = lighten(0.35, danger);
const dangerDark = darken(0.1, danger);
const dangerDarker = darken(0.25, danger);
const dangerDarkest = darken(0.35, danger);
const danger10 = rgba(danger, 0.1);
const danger20 = rgba(danger, 0.2);
const danger30 = rgba(danger, 0.3);
const danger40 = rgba(danger, 0.4);
const danger50 = rgba(danger, 0.5);
const danger60 = rgba(danger, 0.6);
const danger70 = rgba(danger, 0.7);
const danger80 = rgba(danger, 0.8);
const danger90 = rgba(danger, 0.9);

const appBackground = greyBackground;
const surface = white;
const surfaceBorder = greyLighter;
const surfaceRaised = white;
const surfaceRaisedBorder = greyLight;
const hoverSurface = greyLightest;
const disabledSurface = greyLightest;
const tableRowHeader = greyLighter; // header / group rows in tables and planners
const tableRowItem = greyLightest; // sub-item rows in tables and planners
const bodyText = greyDarkest;
const subTextBody = greyDarker;
const subText = greyDark;
const mutedText = grey;
const disabledText = grey;
const iconDefault = greyDark;
const iconOnAccent = white;
const linkText = primary;
// AI chat / inline chart semantic tokens
const aiBrandGradientStart = "#0091ea";
const aiBrandGradientEnd = "#9b51e0";
const chartAxisLabel = greyDarker;
const chartTooltipBackground = white;
const chartSeriesDefault = "#2563eb";
const focusOutline = primary;
const dividerDefault = black20;
const dividerLight = black10;
const shadowColor = black10;

export const colors = {
  /* Greyscale */
  black,
  black90,
  black80,
  black70,
  black60,
  black50,
  black40,
  black30,
  black20,
  black10,
  white,
  white90,
  white80,
  white70,
  white60,
  white50,
  white40,
  white30,
  white20,
  white10,
  grey,
  greyMid,
  greyLight,
  greyLighter,
  greyLightest,
  greyDark,
  greyDarker,
  greyDarkest,
  //
  // Semantic surface / foreground / icon / divider tokens.
  // ----------------------------------------------------
  // Consumers MUST prefer these over referencing raw greyscale tokens
  // (greyDark, greyLighter, ...) or black/white opacity tokens so that
  // light/dark theme switching works consistently. See systemthemeDark.js
  // for the dark-theme values of the same tokens.
  //
  appBackground, // page background
  surface, // cards / sidenav / header / data containers
  surfaceBorder, // border around `surface`
  surfaceRaised, // modal / popover / select / input / menu bg
  surfaceRaisedBorder, // default border around surfaces and on form inputs
  hoverSurface, // subtle tint on hover over a surface
  disabledSurface, // disabled form controls
  tableRowHeader, // header / group rows in tables and planners
  tableRowItem, // sub-item rows in tables and planners
  bodyText, // default text
  subTextBody, // secondary text (labels, captions, legends)
  subText, // secondary text (labels, captions, legends)
  mutedText, // subtle / placeholder text
  disabledText, // disabled text / icons (same as dark theme)
  iconDefault, // generic icon colour
  iconOnAccent, // icon on filled brand bg
  linkText, // default link foreground
  aiBrandGradientStart, // AI brand gradient start (#0091ea blue)
  aiBrandGradientEnd, // AI brand gradient end (#9b51e0 purple)
  chartAxisLabel, // ECharts axis/legend label text
  chartTooltipBackground, // ECharts tooltip background
  chartSeriesDefault, // Default chart series colour
  focusOutline, // focus-ring colour (always brand in both themes)
  dividerDefault, // default hairline / divider
  dividerLight, // lighter hairline / divider
  shadowColor, // translucent-black box-shadow colour
  /* Colours */
  primary,
  primaryLight,
  primaryLighter,
  primaryLightest,
  primaryDark,
  primaryDarker,
  primaryDarkest,
  primary10,
  primary20,
  primary30,
  primary40,
  primary50,
  primary60,
  primary70,
  primary80,
  primary90,
  secondary,
  secondaryLight,
  secondaryLighter,
  secondaryLightest,
  secondaryEvenLighter,
  secondaryDark,
  secondaryDarker,
  secondaryDarkest,
  secondary10,
  secondary20,
  secondary30,
  secondary40,
  secondary50,
  secondary60,
  secondary70,
  secondary80,
  secondary90,
  success,
  successLight,
  successLighter,
  successLightest,
  successEvenLighter,
  successPale,
  successDark,
  successDarker,
  successDarkest,
  success10,
  success20,
  success30,
  success40,
  success50,
  success60,
  success70,
  success80,
  success90,
  warning,
  warningLight,
  warningLighter,
  warningLightest,
  warningPale,
  warningDark,
  warningDarker,
  warningDarkest,
  warning10,
  warning20,
  warning30,
  warning40,
  warning50,
  warning60,
  warning70,
  warning80,
  warning90,
  danger,
  dangerLight,
  dangerLighter,
  dangerLightest,
  dangerDark,
  dangerDarker,
  dangerDarkest,
  danger10,
  danger20,
  danger30,
  danger40,
  danger50,
  danger60,
  danger70,
  danger80,
  danger90,
};

// BORDERS
export const radii = [0, "3px", "6px"];
export const borderWidths = [0, "1px", "2px", "3px"];

// SHADOWS
export const shadows = {
  boxDefault: `0 2px 5px 0 ${colors.shadowColor}`,
  thinOutline: `0 0 0 1px`,
  thickOutline: `0 0 0 3px`,
};

// GRADIENTS
//
// Theme-aware gradient tokens. Consumers MUST reference these rather than
// inlining a linear-gradient() in styled-components templates, so the
// gradient flips correctly when the user switches theme. See
// systemthemeDark.js for the dark-theme values of the same tokens.
export const gradients = {
  // AI Summary panel — pink/purple tint on light, desaturated on dark.
  aiSummarySurface:
    "linear-gradient(to right, rgb(255, 248, 255), rgb(248, 248, 255))",
  aiSummaryHover:
    "linear-gradient(to right, rgb(255, 240, 255), rgb(240, 240, 255))",
  aiSummaryBorder:
    "linear-gradient(to right, rgb(203, 83, 203), rgb(114, 114, 221))",
  // Brand accent gradient — same in both themes (bold brand, not a surface token).
  aiBrand: "linear-gradient(135deg, #0091ea 0%, #9b51e0 100%)",
  // Radial read-through behind floating panel / reactflow canvas titles
  // (e.g. "Reporting Line" on the person hierarchy chart). Fades the
  // surface colour outwards to transparent so nodes behind remain legible.
  canvasTitleReadthrough: `radial-gradient(${colors.surface} 0%, ${rgba(colors.surface, 0.8)} 50%, ${rgba(colors.surface, 0)} 100%)`,
};

// ANIMATION
export const transition = { transitionDefault: "all 200ms ease-in-out" };

// RESPONSIVE MEDIA BREAKPOINTS
export const breakpoints = [
  "450px",
  "675px",
  "900px",
  "1100px",
  "1400px",
  "1900px",
];

export const mediaQueries = {
  screenXS: `@media screen and (min-width: ${breakpoints[0]})`,
  screenS: `@media screen and (min-width: ${breakpoints[1]})`,
  screenM: `@media screen and (min-width: ${breakpoints[2]})`,
  screenL: `@media screen and (min-width: ${breakpoints[3]})`,
  screenXL: `@media screen and (min-width: ${breakpoints[4]})`,
  screenXXL: `@media screen and (min-width: ${breakpoints[5]})`,
};

// APP-SPECIFIC DIMENSIONS

export const appScale = {
  navBarSize: "50px",
  newNavBarSize: "64px",
  navBarLogoSize: "42px",
  sideNavSize: "48px",
  sidebarMaxWidth: "360px",
  sidebarMaxWidthLarge: "450px",
  sidebarMobileHeight: "50vh",
  mobileNavMenuWidth: "250px",
  inputHeightDefault: "32px",
  inputHeightLarge: "52px",
  tabsHeight: "32px",
  treeRowHeight: "32px",
  treeIndent: "16px",
};

export const avatarScale = {
  avatarSmall: "34px",
  avatarDefault: "40px",
  avatarLarge: "70px",
  headerAvatarDefault: "65px",
  headerAvatarLarge: "150px",
  navBarAvatarSize: "38px",
};

export const tagScale = {
  tagHeightDefault: "22px",
  tagHeightSmall: "18px",
  tagPaddingDefault: "3px 7px",
  tagPaddingSmall: "2px 5px",
  tagBorderRadius: "12px",
  tagAvatarSizeDefault: "16px",
  tagAvatarSizeSmall: "14px",
  tagAvatarMarginLeftDefault: "-3px",
  tagAvatarMarginLeftSmall: "-1px",
  tagAvatarFontSizeDefault: "0.8rem",
  tagAvatarFontSizeSmall: "0.7rem",
};

export default {
  fonts,
  font,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  borderWidths,
  space,
  sizes,
  shadows,
  colors,
  gradients,
  breakpoints,
  transition,
  zIndices,
  appScale,
  avatarScale,
  tagScale,
};
